import {
  Component,
  ViewChild,
  ElementRef,
  HostListener,
  AfterViewInit,
  OnDestroy,
  TemplateRef,
} from '@angular/core';
import { SearchFilter, FilterGroup } from 'ldt-people-api';
import * as Highcharts from 'highcharts';

import 'highcharts/modules/exporting';
import 'highcharts/modules/export-data';
import 'highcharts/modules/treemap';
import 'highcharts/modules/sankey';

import { MatSidenav } from '@angular/material/sidenav';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { map, Observable, Subscription } from 'rxjs';
import * as dayjs from 'dayjs';
import { ActivatedRoute } from '@angular/router';
import { UIOrg, AuthService } from 'src/app/auth/service/auth.service';
import { JobFunctions, JobLevels } from 'src/app/shared/dw-column';
import { Log } from 'src/app/logger.service';
import {
  QuickBuild,
  UIFilter,
  UIFilterGroup,
  UniqueIdGenerator,
} from 'src/app/people/quick-build.service';
import { DateRangePickerComponent } from 'src/app/shared/date-range-picker/date-range-picker.component';
import { ShareThisViewService } from 'src/app/shared/share-this-view/share-this-view.service';
import { StreamsSubscribeDrawerService } from 'src/app/shared/streams-subscribe-drawer/streams-subscribe-drawer.service';
import { SavedListCompanyItem } from 'src/app/shared/saved-lists/saved-lists-models';
import {
  CompanyListEditorComponent,
  WorkforceActiveCompanyList,
} from 'src/app/shared/company-list-editor/company-list-editor.component';
import { WorkforceLayoutService } from '../workforce-layout.service';
import { WorkforceAppComponent } from '../app.wf.component';
import {
  PrevNextFlowComparisonConfig,
  HeadcountComparisonConfig,
  TenureCurrentBreakdownConfig,
  JobStartsAndEndsConfig,
  PrevNextFlowConfig,
  DemographicsConfig,
  DemographicsCurrentConfig,
  LocationMapConfig,
  TopValuesComparisonConfig,
  JobsMovesComparisonConfig,
  HistoricalTenureComparisonConfig,
  TenureCohortComparisonConfig,
} from 'src/app/shared/report-visualizations/interfaces/report-config.interfaces';
import { LoginDialogService } from 'src/app/auth/service/login-dialog.service';
import { AccessLinksService, CreateAccessLinkRequest } from 'ldt-identity-service-api';
import { ExportingMimeTypeValue } from 'highcharts';
import { ClipboardService } from 'ngx-clipboard';
import { NotificationService } from 'src/app/shared/notification-service/notification.service';
import { PeopleReportService } from 'src/app/people/report-service.service';
import { HeapUtils } from 'src/app/api-client/heap.utils';
import { InsightsNavigationService } from './insights-navigation.service';
import {
  RoleDefinition,
  RoleDefinitionsService,
} from 'src/app/core/services/role-definitions.service';

interface PageState {
  activeList: WorkforceActiveCompanyList | undefined;
  dateRangeStrings: [string, string];
  selectedJobFunctions: JobFunctions[];
  selectedJobLevels: JobLevels[];
  selectedRole: RoleDefinition | null;
  mode: 'company' | 'battle' | 'benchmark';
  selectedCompanyIds: string[];
  chartConfigs: ChartConfigurations;
}

// Chart configurations by chart ID
interface ChartConfigurations {
  'talent-flow'?: PrevNextFlowConfig;
  'job-starts-and-ends'?: JobStartsAndEndsConfig;
  'demographics-over-time'?: DemographicsConfig;
  'current-demographics'?: DemographicsCurrentConfig;
  'location-map'?: LocationMapConfig;
  'tenure-current-breakdown'?: TenureCurrentBreakdownConfig;
  'headcount-comparison'?: HeadcountComparisonConfig;
  'top-values-comparison'?: TopValuesComparisonConfig;
  'job-moves-comparison'?: JobsMovesComparisonConfig;
  'historical-tenure-comparison'?: HistoricalTenureComparisonConfig;
  'talent-flow-comparison'?: PrevNextFlowComparisonConfig;
  'tenure-cohort-comparison'?: TenureCohortComparisonConfig;
}

@Component({
  selector: 'app-workforce-insights',
  templateUrl: './insights.component.html',
  styleUrls: ['./insights.component.scss'],
  standalone: false,
})
export class WorkforceInsightsComponent implements AfterViewInit, OnDestroy {
  @ViewChild('regularFilters') regularFilters!: ElementRef;
  @ViewChild(CompanyListEditorComponent) companyListEditor!: CompanyListEditorComponent;
  @ViewChild('companyEditorDialog') companyEditorDialog!: TemplateRef<any>;

  // org stuff
  orgSettings: Observable<UIOrg | undefined>;
  public orgs: UIOrg[] = [];
  private orgsListSubscription: Subscription;

  userEmail: string | undefined;
  userInitial: string | undefined;
  private userSubscription: Subscription;

  mode: 'company' | 'battle' | 'benchmark' = 'battle';
  battleModeDisplayNames = '';
  remainingCompaniesCount = 0;
  remainingCompanyNames: string[] = [];

  selectedCompanyIds: Set<string> = new Set();
  selectMultipleCompanies: 'on' | 'toggle' = 'on';
  selectedCompanies: Set<SavedListCompanyItem> = new Set();

  private readonly LOCAL_STORAGE_KEY = 'workforce-page-state';
  private readonly LOCAL_STORAGE_KEY_TOUR_STATE = 'workforce-tour-seen';

  activeList: WorkforceActiveCompanyList = {
    id: null,
    name: '',
    companies: [],
  };

  quickBuild: QuickBuild | null = null;
  companyBattleQBs: QuickBuild[] = [];

  // Benchmark mode QuickBuilds
  benchmarkCompanyQBs: QuickBuild[] = [];

  reportTarget: string = 'moneyball';

  dateRangeStrings: [string, string] = [
    dayjs().subtract(2, 'year').format('YYYY-MM'),
    dayjs().format('YYYY-MM'),
  ];

  selectedJobFunctions: JobFunctions[] | undefined = undefined;
  selectedJobLevels: JobLevels[] | undefined = undefined;

  Highcharts: typeof Highcharts = Highcharts;

  // thin left-sidesidenav
  isSmallScreen$ = this.layoutService.isSmallScreen$;

  tourSubscription: Subscription;

  showStickyFilters = false;
  private filtersObserver!: IntersectionObserver;

  chartConfigs: ChartConfigurations = {};

  private companyEditorDialogRef!: MatDialogRef<any>;
  private palModeClickHandler: ((event: Event) => void) | null = null;

  isPalMode: boolean = false;
  al_created_at: string = '';

  constructor(
    private authService: AuthService,
    private shareThisViewService: ShareThisViewService,
    private drawerService: StreamsSubscribeDrawerService,
    private route: ActivatedRoute,
    public dialog: MatDialog,
    public layoutService: WorkforceLayoutService,
    private parent: WorkforceAppComponent,
    private loginDialogService: LoginDialogService,
    private accessLinksService: AccessLinksService,
    private clipboardService: ClipboardService,
    private notify: NotificationService,
    private peopleReportService: PeopleReportService
  ) {}

  ngOnInit() {
    this.orgSettings = this.authService.$getSelectedOrg;

    this.orgs = this.authService.getOrgsValue || [];
    this.orgsListSubscription = this.authService.$getOrgs.subscribe((orgsList) => {
      this.orgs = orgsList || []; // for the org picker
    });

    this.userEmail = this.authService.getUserValue?.email;
    if (this.authService.getUserValue?.name && this.authService.getUserValue.name.length > 0) {
      this.userInitial = this.authService.getUserValue.name.charAt(0).toUpperCase();
    }

    this.userSubscription = this.authService.$getUser.subscribe((user) => {
      this.userEmail = user?.email;
      if (user?.name && user.name.length > 0) {
        this.userInitial = user.name.charAt(0).toUpperCase();
      } else {
        this.userInitial = undefined; // if name is not available
      }
    });

    this.authService.$palMode.subscribe((palMode) => {
      this.isPalMode = palMode;
      if (palMode) {
        this.addClickInterception();
      } else {
        this.removeClickInterception();
        this.updateQuickBuild();
      }
    });

    // WF Insights-specific export options
    const component = this;
    Highcharts.setOptions({
      exporting: {
        buttons: {
          contextButton: {
            menuItems: [
              'downloadPNGWithLink',
              'downloadJPEGWithLink',
              'downloadSVGWithLink',
              'separator',
              'downloadCSV',
              'downloadXLS',
            ],
          },
        },
        menuItemDefinitions: {
          downloadPNG: {
            text: 'Download PNG',
            onclick: function () {
              WorkforceInsightsComponent.downloadChart(this, component, 'png', false);
            },
          },
          downloadJPEG: {
            text: 'Download JPEG',
            onclick: function () {
              WorkforceInsightsComponent.downloadChart(this, component, 'jpeg', false);
            },
          },
          downloadSVG: {
            text: 'Download SVG',
            onclick: function () {
              WorkforceInsightsComponent.downloadChart(this, component, 'svg', false);
            },
          },
          downloadPNGWithLink: {
            text: 'Download PNG with Link',
            onclick: function () {
              WorkforceInsightsComponent.downloadChart(this, component, 'png');
            },
          },
          downloadJPEGWithLink: {
            text: 'Download JPEG with Link',
            onclick: function () {
              WorkforceInsightsComponent.downloadChart(this, component, 'jpeg');
            },
          },
          downloadSVGWithLink: {
            text: 'Download SVG with Link',
            onclick: function () {
              WorkforceInsightsComponent.downloadChart(this, component, 'svg');
            },
          },
          downloadCSV: {
            text: 'Download Data as CSV',
          },
          downloadXLS: {
            text: 'Download Data as XLS',
          },
        },
      },
    });

    // Process navigation state on initial load
    this.processNavigationState();

    // Update the quick build based on the determined state
    this.updateQuickBuild();

    // See if there is a querystring param called "showTour" or localstorage
    const showTour = this.route.snapshot.queryParamMap.get('showTour');
    const tourState = localStorage.getItem(this.LOCAL_STORAGE_KEY_TOUR_STATE);
    // Disabling the tour for now because it's been a bit buggy
    // if (showTour || !tourState) {
    //   this.tourService.start();
    //   localStorage.setItem(this.LOCAL_STORAGE_KEY_TOUR_STATE, 'true');
    // }
  }

  ngAfterViewInit() {
    // to toggle sticky filters when scrolling
    this.filtersObserver = new IntersectionObserver(
      ([entry]) => {
        this.showStickyFilters = !entry.isIntersecting;
      },
      { threshold: 0 }
    );
    if (this.regularFilters?.nativeElement) {
      this.filtersObserver.observe(this.regularFilters.nativeElement);
    }
  }

  private addClickInterception() {
    this.removeClickInterception();

    this.palModeClickHandler = (event: Event) => {
      // Check if the click target is inside the login dialog, in which case we allow it
      const target = event.target as Element;
      const isInsideLoginDialog =
        target.closest('.login-dialog-container') ||
        target.closest('.mat-mdc-dialog-container') ||
        target.closest('.mat-dialog-container') ||
        target.closest('.login-dialog-backdrop') ||
        target.closest('#login-signup') ||
        target.closest('.login-signup-main');

      if (!isInsideLoginDialog) {
        this.loginDialogService.openLoginDialog();
        HeapUtils.track('public_share_view_signup_dialog_shown', {});
        event.stopPropagation();
        event.preventDefault();
      }
    };

    document.addEventListener('click', this.palModeClickHandler, true);
  }

  private removeClickInterception() {
    if (this.palModeClickHandler) {
      document.removeEventListener('click', this.palModeClickHandler, true);
      this.palModeClickHandler = null;
    }
  }

  static downloadChart(
    chart: Highcharts.Chart,
    component: WorkforceInsightsComponent,
    type: 'png' | 'jpeg' | 'svg',
    showLink: boolean = true
  ) {
    const extension: ExportingMimeTypeValue =
      type === 'png' ? 'image/png' : type === 'jpeg' ? 'image/jpeg' : 'image/svg+xml';

    if (!showLink) {
      chart.exporting.exportChart(
        {
          type: extension,
          filename: 'workforce-insights',
        },
        {
          caption: {
            text: undefined,
          },
        }
      );
      return;
    }

    component.getPublicLink().subscribe((token) => {
      if (!token) {
        component.notify.error('Unable to create public link');
        return;
      }
      chart.exporting.exportChart(
        {
          type: extension,
          filename: 'workforce-insights',
        },
        {
          caption: {
            text: 'app.workforce.ai/go/' + token,
          },
        }
      );

      component.clipboardService.copy('https://app.workforce.ai/go/' + token);
      component.notify.info(
        'Image downloaded.  Public link for sharing copied to your clipboard.',
        4000
      );
    });
  }

  onSelectedCompaniesChange(companies: Set<SavedListCompanyItem>) {
    // Check if the new list is the same as current - if so, return early
    if (
      this.selectedCompanies.size === companies.size &&
      [...this.selectedCompanies].every((company) => companies.has(company))
    ) {
      return;
    }

    this.selectedCompanies = companies;
    this.saveLocalState();
    this.updateQuickBuild();
  }

  onActiveListChange(activeList: WorkforceActiveCompanyList) {
    this.activeList = activeList;
    this.saveLocalState();
    this.updateQuickBuild();
  }

  onDateRangeChanged(event: [moment.Moment, moment.Moment]) {
    this.dateRangeStrings = DateRangePickerComponent.DateRangePickerDatesToFilterDates(event);

    this.saveLocalState();
    this.updateQuickBuild();
  }

  onJobFunctionSelectionChanged(value: string[]) {
    this.selectedJobFunctions = value.map((v) => v as JobFunctions);

    this.saveLocalState();
    this.updateQuickBuild();
  }

  onJobLevelSelectionChanged(value: string[]) {
    this.selectedJobLevels = value.map((v) => v as JobLevels);

    this.saveLocalState();
    this.updateQuickBuild();
  }

  selectedRole: RoleDefinition | null = null;
  onRoleSelect(role: RoleDefinition) {
    this.selectedRole = role;
    this.saveLocalState();
    this.updateQuickBuild();
  }

  onChartConfigChange(chartId: keyof ChartConfigurations, config: any) {
    this.chartConfigs[chartId] = config;
    this.saveLocalState();
  }

  setMode(newMode: 'company' | 'battle' | 'benchmark') {
    const previousMode = this.mode;
    this.mode = newMode;

    // Track mode toggle in Heap
    HeapUtils.track('workforce_insights_mode_toggle', {
      previous_mode: previousMode,
      new_mode: newMode,
      page: 'insights',
    });

    if (this.activeList.companies.length === 0) {
      return;
    }

    // Set the selected companies based on the mode and current selections
    switch (this.mode) {
      case 'company':
        this.selectMultipleCompanies = 'toggle';
        this.companyListEditor?.selectOne();
        break;
      case 'battle':
        this.selectMultipleCompanies = 'on';
        this.companyListEditor?.selectAll();
        break;
      case 'benchmark':
        // In benchmark mode, we need at least 2 companies (1 target + 1+ benchmarks)
        this.selectMultipleCompanies = 'on';
        if (this.selectedCompanies.size === 0 || this.selectedCompanies.size === 1) {
          this.companyListEditor?.selectAll();
        }
        break;
    }

    this.saveLocalState();
    this.updateQuickBuild();
  }

  updateQuickBuild() {
    if (this.selectedCompanies.size === 0) {
      this.quickBuild = null;
      this.battleModeDisplayNames = '';
      this.benchmarkCompanyQBs = [];
      return;
    }

    this.peopleReportService.startCacheContext();

    if (this.mode === 'company') {
      this.generateQuickBuildForCompanies(Array.from(this.selectedCompanies));
      return;
    }

    if (this.mode === 'battle') {
      this.generateCompanyBattleQBs();
      return;
    }

    if (this.mode === 'benchmark') {
      this.generateBenchmarkQBs();
      return;
    }
  }

  generateCompanyBattleQBs() {
    if (this.selectedCompanies.size === 0) {
      this.companyBattleQBs = [];
      this.battleModeDisplayNames = '';
      this.remainingCompaniesCount = 0;
      this.remainingCompanyNames = [];
      return;
    }

    this.companyBattleQBs = Array.from(this.selectedCompanies).map((c) =>
      this.generateQuickBuildForCompanies([c])
    );

    this.getBattleCompanyNamesForUI();
  }

  getBattleCompanyNamesForUI() {
    const companyNames = Array.from(this.selectedCompanies).map((c) => c.name!);
    const count = companyNames.length;

    if (count <= 2) {
      this.battleModeDisplayNames = companyNames.join(' vs ');
      this.remainingCompaniesCount = 0;
      this.remainingCompanyNames = [];
    } else {
      this.battleModeDisplayNames = companyNames.slice(0, 2).join(', ');
      this.remainingCompaniesCount = count - 2;
      this.remainingCompanyNames = companyNames.slice(2);
    }
  }

  generateBenchmarkQBs() {
    if (this.selectedCompanies.size === 0) {
      this.benchmarkCompanyQBs = [];
      return;
    }

    const companiesArray = Array.from(this.selectedCompanies);

    this.benchmarkCompanyQBs = companiesArray.map((c) => this.generateQuickBuildForCompanies([c]));
    this.getBattleCompanyNamesForUI();
  }

  generateQuickBuildForCompanies(companies: SavedListCompanyItem[]): QuickBuild {
    const filters: (UIFilter | UIFilterGroup)[] = [];

    // Company filter
    filters.push({
      filter: {
        field: 'jobs_search',
        match_type: SearchFilter.MatchTypeEnum.Exact,
        string_values: companies.map((c) => c.id! + (c.group ? '-group' : '')),
        type: SearchFilter.TypeEnum.Must,
      },
    });

    // Functions filter
    if (
      this.selectedJobFunctions &&
      this.selectedJobFunctions.length > 0 &&
      this.selectedJobFunctions.length !== Object.values(JobFunctions).length
    ) {
      filters.push({
        filter: {
          field: 'jobs.function',
          match_type: SearchFilter.MatchTypeEnum.Exact,
          string_values: this.selectedJobFunctions,
          type: SearchFilter.TypeEnum.Must,
        },
      });
    }

    // Levels filter
    if (
      this.selectedJobLevels &&
      this.selectedJobLevels.length > 0 &&
      this.selectedJobLevels.length !== Object.values(JobLevels).length
    ) {
      filters.push({
        filter: {
          field: 'jobs.level',
          match_type: SearchFilter.MatchTypeEnum.Exact,
          string_values: this.selectedJobLevels,
          type: SearchFilter.TypeEnum.Must,
        },
      });
    }

    if (this.selectedRole) {
      filters.push({
        filter: {
          field: 'job_role',
          name: this.selectedRole.name,
          id: this.selectedRole.id,
          filters: RoleDefinitionsService.toQuickBuildFilterGroup(this.selectedRole),
        },
      });
    }

    const quickBuild: QuickBuild = {
      id: UniqueIdGenerator.generate(),
      name: companies.map((c) => c.name!).join(', '),
      rootFilterGroup: {
        id: UniqueIdGenerator.generate(),
        operator: FilterGroup.OperatorEnum.And,
        filters: [
          {
            id: 'moneyball',
            operator: FilterGroup.OperatorEnum.And,
            isJobsGroup: true,
            filters: filters,
          },
        ],
      },
    };

    this.quickBuild = quickBuild;
    return quickBuild;
  }

  getCurrentState(): PageState {
    const pageState: PageState = {
      activeList: this.activeList,
      dateRangeStrings: this.dateRangeStrings,
      selectedJobFunctions: this.selectedJobFunctions || [],
      selectedJobLevels: this.selectedJobLevels || [],
      selectedRole: this.selectedRole,
      mode: this.mode,
      selectedCompanyIds: Array.from(this.selectedCompanyIds),
      chartConfigs: this.chartConfigs,
    };
    return pageState;
  }

  private saveLocalState() {
    const pageState = this.getCurrentState();
    localStorage.setItem(this.LOCAL_STORAGE_KEY, JSON.stringify(pageState));
  }

  private applyPageState(pageState: PageState) {
    if (pageState.activeList) {
      this.activeList = pageState.activeList;
    }
    if (pageState.dateRangeStrings) {
      this.dateRangeStrings = pageState.dateRangeStrings;
    }
    this.selectedJobFunctions = pageState.selectedJobFunctions;
    this.selectedJobLevels = pageState.selectedJobLevels;
    this.selectedRole = pageState.selectedRole;
    this.setMode(pageState.mode);
    this.selectedCompanyIds = new Set(pageState.selectedCompanyIds);

    // Restore chart configurations
    if (pageState.chartConfigs) {
      this.chartConfigs = pageState.chartConfigs;
    }
  }

  private restoreLocalState(): boolean {
    try {
      const savedState = localStorage.getItem(this.LOCAL_STORAGE_KEY);
      if (savedState) {
        const parsedState: PageState = JSON.parse(savedState);
        this.applyPageState(parsedState);
        this.saveLocalState();
        return true;
      }
    } catch (error) {
      Log.s('Error restoring local state', 'error', {
        error: error?.toString() || 'Unknown error',
      });
    }
    return false;
  }

  onShareClick() {
    if (this.mode === 'benchmark') {
      HeapUtils.track('benchmark_mode_state_sharing_not_allowed', {});

      this.notify.info('Sharing is not yet supported in benchmark mode. Check back soon!');
      return;
    }

    const state = JSON.parse(JSON.stringify(this.getCurrentState())) as PageState;

    // Add the report cache for the current page to the state
    const config = {
      ...state,
      dataCache: this.peopleReportService.getContextCache(),
    };

    this.shareThisViewService.showShareDialog(
      this.authService.getSelectedOrgIdValue,
      '/workforce/insights',
      config
    );
  }

  getPublicLink(): Observable<string | null> {
    const state = JSON.parse(JSON.stringify(this.getCurrentState())) as PageState;

    // Add the report cache for the current page to the state
    const config = {
      ...state,
      dataCache: this.peopleReportService.getContextCache(),
    };

    // We'll anonymize a couple things here.
    config.activeList!.name = 'Public Share';
    config.activeList!.companies = config.activeList!.companies.filter((c) =>
      config.selectedCompanyIds.includes(c.id!)
    );

    let req: CreateAccessLinkRequest = {
      type: CreateAccessLinkRequest.TypeEnum.SharedView,
      config: config,
      redirect_url: '/insights',
      scope: CreateAccessLinkRequest.ScopeEnum.Public,
    };
    return this.accessLinksService
      .createAccessLink(this.authService.getSelectedOrgIdValue, req)
      .pipe(map((res) => res.token));
  }

  /**
   * Generates a URL with the current page state encoded as a query parameter.
   * This URL can be used to deep-link directly to a specific insights view.
   * Currently here for reference only and not used anywhere
   */
  getStateUrl(baseUrl?: string): string {
    const state = this.getCurrentState();
    const encodedState = btoa(JSON.stringify(state));
    const base = baseUrl || `${window.location.origin}/workforce/insights`;
    return `${base}?state=${encodedState}`;
  }

  private processNavigationState() {
    // Access navigation state via history.state
    const navigationState = history.state as {
      al_config?: PageState;
      al_created_at?: string;
      fromChat?: boolean;
      threadId?: string;
      companyLookups?: { [key: string]: string };
      mode?: 'company' | 'battle' | 'benchmark';
      selectedCompanyId?: string;
      selectedCompanyIds?: string[];
      selectedJobFunctions?: JobFunctions[];
      selectedJobLevels?: JobLevels[];
      selectedRole?: RoleDefinition;
    };

    // Check for query string parameter first (highest priority)
    const stateParam = this.route.snapshot.queryParamMap.get('state');
    if (stateParam) {
      try {
        const decodedState = atob(stateParam);
        const parsedState: PageState = JSON.parse(decodedState);
        Log.d('Restoring state from query parameter:', parsedState);
        this.applyPageState(parsedState);

        // Clear the query parameter
        history.replaceState({}, '', window.location.pathname);
        this.saveLocalState();
      } catch (error) {
        Log.s('Error parsing state from query parameter', 'error', {
          error: error?.toString() || 'Unknown error',
        });
        // Fall through to other state restoration methods
        if (navigationState?.al_config) {
          Log.d('Restoring state from navigation config:', navigationState.al_config);
          this.applyPageState(navigationState.al_config);
          history.replaceState({}, '', window.location.pathname);
          this.saveLocalState();
        } else {
          this.restoreLocalState();
        }
      }
    }
    // Try to load page state if someone is coming from an access link
    else if (navigationState?.al_config) {
      Log.d('Restoring state from navigation config:', navigationState.al_config);
      this.applyPageState(navigationState.al_config);

      // clear the navigation state
      history.replaceState({}, '', window.location.pathname);
      this.saveLocalState();
    } else if (navigationState?.fromChat && navigationState?.companyLookups) {
      // Handle navigation from chat (when not on chat page)
      this.loadCompaniesFromChat(
        navigationState.companyLookups,
        navigationState.mode || 'company',
        navigationState.selectedCompanyId,
        navigationState.selectedCompanyIds
      );

      // Apply additional filters from chat if present, otherwise clear them
      if (navigationState.selectedJobFunctions && navigationState.selectedJobFunctions.length > 0) {
        this.selectedJobFunctions = navigationState.selectedJobFunctions;
      } else {
        this.selectedJobFunctions = [];
      }
      if (navigationState.selectedJobLevels && navigationState.selectedJobLevels.length > 0) {
        this.selectedJobLevels = navigationState.selectedJobLevels;
      } else {
        this.selectedJobLevels = [];
      }
      if (navigationState.selectedRole) {
        this.selectedRole = navigationState.selectedRole;
      } else {
        this.selectedRole = null;
      }

      // clear the navigation state
      history.replaceState({}, '', window.location.pathname);
      this.saveLocalState();
    } else {
      this.restoreLocalState();
    }

    if (navigationState?.al_created_at) {
      this.al_created_at = dayjs(navigationState.al_created_at).format('MM/DD/YYYY');
    }
  }

  ngOnDestroy() {
    this.tourSubscription?.unsubscribe();
    if (this.filtersObserver) {
      this.filtersObserver.disconnect();
    }
    this.orgsListSubscription?.unsubscribe();
    this.userSubscription?.unsubscribe();
  }

  // mobile only
  openCompanyEditorModal(): void {
    if (this.isPalMode) {
      return;
    }
    this.companyEditorDialogRef = this.dialog.open(this.companyEditorDialog, {
      width: '100%',
      maxWidth: '100%',
      height: '100%',
      maxHeight: '100%',
    });
  }

  // mobile only (we need it b/c we import both legacy and new mat dialogs)
  closeCompanyEditorModal(): void {
    if (this.companyEditorDialogRef) {
      this.companyEditorDialogRef.close();
    }
  }

  // used in sticky filters chip
  get activeFilterCount(): number {
    let count = 0;
    const allJobFunctions = Object.values(JobFunctions);
    const allJobLevels = Object.values(JobLevels);

    if (
      this.selectedJobFunctions &&
      this.selectedJobFunctions.length > 0 &&
      this.selectedJobFunctions.length < allJobFunctions.length
    ) {
      count++;
    }
    if (
      this.selectedJobLevels &&
      this.selectedJobLevels.length > 0 &&
      this.selectedJobLevels.length < allJobLevels.length
    ) {
      count++;
    }
    if (this.selectedRole) {
      count++;
    }
    return count;
  }

  // for UI only to highlight the active filter
  get isJobFunctionFilterActive(): boolean {
    const allJobFunctions = Object.values(JobFunctions);
    return !!(
      this.selectedJobFunctions &&
      this.selectedJobFunctions.length > 0 &&
      this.selectedJobFunctions.length < allJobFunctions.length
    );
  }

  // for UI only to highlight the active filter
  get isJobLevelFilterActive(): boolean {
    const allJobLevels = Object.values(JobLevels);
    return !!(
      this.selectedJobLevels &&
      this.selectedJobLevels.length > 0 &&
      this.selectedJobLevels.length < allJobLevels.length
    );
  }

  get isRoleFilterActive(): boolean {
    return !!this.selectedRole;
  }

  get formattedDateRange(): string {
    const [from, to] = this.dateRangeStrings;
    return `${dayjs(from).format('MMM YYYY')} — ${dayjs(to).format('MMM YYYY')}`;
  }

  toggleThinSidenav() {
    this.parent.toggleThinSidenav();
  }

  switchOrg(orgIdToSelect: string) {
    this.authService.setSelectedOrgId(orgIdToSelect);
  }

  logOut() {
    this.authService.logOut();
  }

  // Load companies from chat navigation state
  private loadCompaniesFromChat(
    companyLookups: { [key: string]: string },
    mode: 'company' | 'battle' | 'benchmark',
    selectedCompanyId?: string,
    selectedCompanyIds?: string[]
  ) {
    // Convert companyLookups to SavedListCompanyItem array
    const companies: SavedListCompanyItem[] = Object.entries(companyLookups).map(([name, id]) => ({
      id: id.replace('-group', ''),
      name: name,
      group: id.includes('-group'),
    }));

    // Set the active list
    this.activeList = {
      id: null,
      name: 'From Chat',
      companies: companies,
    };

    // Set selected companies based on provided IDs or mode
    if (selectedCompanyIds && selectedCompanyIds.length > 0) {
      // Battle mode - multiple companies explicitly specified
      const selectedCompaniesList = companies.filter((c) => selectedCompanyIds.includes(c.id!));
      if (selectedCompaniesList.length > 0) {
        this.selectedCompanies = new Set(selectedCompaniesList);
        this.selectedCompanyIds = new Set(selectedCompanyIds);
      } else {
        // Fallback: select all companies if specified IDs not found
        this.selectedCompanies = new Set(companies);
        this.selectedCompanyIds = new Set(companies.map((c) => c.id!));
      }
    } else if (mode === 'company' && selectedCompanyId) {
      // In company mode with a specific selection, only select that company
      const selectedCompany = companies.find((c) => c.id === selectedCompanyId);
      if (selectedCompany) {
        this.selectedCompanies = new Set([selectedCompany]);
        this.selectedCompanyIds = new Set([selectedCompanyId]);
      } else {
        // Fallback: select first company if specified ID not found
        if (companies.length > 0) {
          this.selectedCompanies = new Set([companies[0]]);
          this.selectedCompanyIds = new Set([companies[0].id!]);
        } else {
          this.selectedCompanies = new Set();
          this.selectedCompanyIds = new Set();
        }
      }
    } else {
      // In battle mode or no specific selection, select all companies
      this.selectedCompanies = new Set(companies);
      this.selectedCompanyIds = new Set(companies.map((c) => c.id!));
    }

    // Set the mode
    this.mode = mode;
    this.selectMultipleCompanies = mode === 'company' ? 'toggle' : 'on';
  }
}
